import Animated, {
    useAnimatedStyle,
    useSharedValue,
    runOnJS,
    withTiming,
} from "react-native-reanimated";
import { DropdownConfigType } from "../contexts/DropdownManager";
import React, { ComponentProps, useCallback, useEffect, useMemo } from "react";
import { StyleSheet, useWindowDimensions, Pressable, View, ScrollView } from "react-native";
import { useTheme } from "../hooks/use-theme";
import Icon from "./Icon";
import Text from "./Text";
import { getShadow } from "../utils";
import { OutsidePressHandler } from "./OutsidePressHandler";

type DropdownProps = {
    items: NonNullable<DropdownConfigType["items"]>;
    position: NonNullable<DropdownConfigType["position"]>;
    onClose: () => void;
    onBlur?: () => void;
    onLayout?: ComponentProps<typeof ScrollView>["onLayout"];
    keepOpen?: boolean;
};

const DROPDOWN_OFFSET = 12;
const SCREEN_HORIZONTAL_PADDING = 40;

const timingConfig = {
    duration: 200,
};

export const ITEM_HEIGHT = 44;

export const Dropdown = ({
    items,
    position,
    onLayout,
    onClose,
    onBlur,
    keepOpen,
}: DropdownProps) => {
    const styles = useStyles();
    const { colors } = useTheme();
    const { width: windowWidth } = useWindowDimensions();

    const { dropdownLeft, dropdownRight, dropdownTop } = useMemo(() => {
        const { x, width, pressX, y } = position;

        const left = pressX;
        const right = windowWidth - x - width;

        return { dropdownLeft: left, dropdownRight: right, dropdownTop: y };
    }, [windowWidth, position]);

    const topFactor = useSharedValue(dropdownTop - DROPDOWN_OFFSET);
    const opacityFactor = useSharedValue(0);
    const animatedStyles = useAnimatedStyle(() => ({
        top: topFactor.value,
        opacity: opacityFactor.value,
    }));

    const fadeOut = useCallback(() => {
        onBlur?.();
        topFactor.value = withTiming(dropdownTop - DROPDOWN_OFFSET, timingConfig);
        opacityFactor.value = withTiming(0, timingConfig, () => {
            runOnJS(onClose)();
        });
    }, [onBlur, topFactor, dropdownTop, opacityFactor, onClose]);

    const hasSelectable = useMemo(() => items.some((e) => e.selected !== undefined), [items]);

    useEffect(() => {
        topFactor.value = withTiming(dropdownTop, timingConfig);
        opacityFactor.value = withTiming(1, timingConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownTop]);

    return (
        <Animated.View
            style={[
                styles.container,
                position.dropdownPosition === "left"
                    ? { right: dropdownRight }
                    : { left: dropdownLeft },
                {
                    maxWidth: windowWidth - SCREEN_HORIZONTAL_PADDING,
                    maxHeight: position.maxHeight,
                },
                animatedStyles,
            ]}
        >
            <OutsidePressHandler onOutsidePress={fadeOut}>
                <ScrollView onLayout={onLayout}>
                    {items.map((item) => (
                        <Pressable
                            key={item.label}
                            style={[styles.item, item.style]}
                            onPress={() => {
                                void item.onClick();
                                if (!keepOpen) {
                                    fadeOut();
                                }
                            }}
                            testID={item.testID}
                        >
                            {item.icon && (
                                <Icon
                                    size={24}
                                    name={item.icon}
                                    color={item.color}
                                    style={[styles.icon]}
                                />
                            )}
                            <View style={[styles.label]}>
                                {typeof item.label === "string" ? (
                                    <Text
                                        numberOfLines={item.multiline ? undefined : 1}
                                        style={[
                                            styles.labelText,
                                            !!item.color && { color: item.color },
                                        ]}
                                    >
                                        {item.label}
                                    </Text>
                                ) : (
                                    item.label
                                )}
                            </View>
                            {hasSelectable && (
                                <Icon
                                    name="check"
                                    size={24}
                                    style={styles.selectableIcon}
                                    color={
                                        item.selected ? item.color || colors.message : "transparent"
                                    }
                                />
                            )}
                        </Pressable>
                    ))}
                </ScrollView>
            </OutsidePressHandler>
        </Animated.View>
    );
};

const useStyles = () => {
    const { roundness, fonts, gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    position: "absolute",
                    backgroundColor: "white",
                    borderRadius: roundness,
                    ...getShadow({ level: 8 }),
                },
                item: {
                    //height: ITEM_HEIGHT,
                    display: "flex",
                    flexDirection: "row",
                    paddingHorizontal: 10,
                    alignItems: "flex-start",
                    overflow: "hidden",
                    paddingVertical: gridSize / 4,
                },
                icon: {
                    width: 24,
                    marginRight: 12,
                    flexGrow: 0,
                },
                label: {
                    flexShrink: 1,
                    flexGrow: 1,
                    flex: 0,
                },
                labelText: {
                    ...fonts.bodyMedium,
                    marginRight: gridSize / 2,
                },
                selectableIcon: {
                    flexGrow: 0,
                    flexShrink: 0,
                    width: 24,
                },
            }),
        [roundness, fonts.bodyMedium, gridSize],
    );
};
